import { useState, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, MoreVertical } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field, FieldDescription, FieldGroup, FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { SearchableSelect } from '@/components/SearchableSelect'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  NONE, extractErr, RecordDrawer, useDeleteConfirm,
} from '@/components/ui/inline-edit'

// ─────────────────── Types ───────────────────
type EventDef = {
  id: number
  code: string
  label: string
  category: string
  description: string
  placeholders: string[]
}

type Rule = {
  id: number
  name: string
  description: string
  trigger_event: number
  trigger_event_code: string
  trigger_event_label: string
  trigger_event_category: string
  recipient_kind: 'USER' | 'ROLE' | 'DYNAMIC'
  recipient_users: number[]
  recipient_roles: number[]
  recipient_resolver: string
  channels: string[]
  is_active: boolean
  is_system: boolean
  created_at: string
  updated_at: string
}

const KIND_LABEL: Record<Rule['recipient_kind'], string> = {
  USER: 'Specific users',
  ROLE: 'Users with role',
  DYNAMIC: 'Dynamic',
}

const CHANNEL_LABEL: Record<string, string> = {
  IN_APP: 'In-app',
  EMAIL: 'Email',
}

export default function AdminNotificationRules() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { confirm, dialog } = useDeleteConfirm()

  const rulesQ = useQuery({
    queryKey: ['notif-rules'],
    queryFn: async () => (await api.get<Rule[]>('/notifications/rules/')).data,
  })
  const eventsQ = useQuery({
    queryKey: ['notif-events'],
    queryFn: async () => (await api.get<EventDef[]>('/notifications/events/')).data,
  })

  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<{ name: string; trigger_event: number | null }>({
    name: '', trigger_event: null,
  })

  const createMut = useMutation({
    mutationFn: async () => {
      if (!draft.name.trim()) throw new Error('Name is required')
      if (!draft.trigger_event) throw new Error('Pick a trigger event')
      return (await api.post<Rule>('/notifications/rules/', {
        name: draft.name.trim(),
        trigger_event: draft.trigger_event,
        recipient_kind: 'USER',
        recipient_users: [],
        recipient_roles: [],
        channels: ['IN_APP'],
        subject_template: '',
        body_text_template: '',
        body_html_template: '',
        filter_json: {},
        is_active: true,
      })).data
    },
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['notif-rules'] })
      toast.success('Rule created — fill in recipients + templates on the next page')
      setCreating(false)
      setDraft({ name: '', trigger_event: null })
      navigate(`/admin/notifications/rules/${created.id}`)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const toggleActiveMut = useMutation({
    mutationFn: async ({ id, value }: { id: number; value: boolean }) =>
      (await api.patch(`/notifications/rules/${id}/`, { is_active: value })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notif-rules'] })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/notifications/rules/${id}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notif-rules'] })
      toast.success('Rule deleted')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const rules = rulesQ.data || []
  const events = eventsQ.data || []

  // Group rules by event category for display.
  const grouped = new Map<string, Rule[]>()
  for (const r of rules) {
    const cat = r.trigger_event_category || 'Other'
    if (!grouped.has(cat)) grouped.set(cat, [])
    grouped.get(cat)!.push(r)
  }
  const sortedCategories = Array.from(grouped.keys()).sort()

  return (
    <AdminLayout>
      {dialog}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Notification rules</h2>
          <p className="text-sm text-muted-foreground">
            {rules.length} total — {events.length} event{events.length === 1 ? '' : 's'} available.
            Rules route events to recipients via in-app + email.
          </p>
        </div>
        <Button onClick={() => setCreating(true)}><Plus className="h-3 w-3 mr-1" /> New rule</Button>
      </div>

      {rulesQ.isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : rules.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">
          No rules yet. Pick an event from the catalog and add your first one.
        </div>
      ) : (
        <div className="space-y-6">
          {sortedCategories.map((cat) => (
            <div key={cat} className="bg-card rounded-lg shadow overflow-hidden">
              <div className="px-4 py-2 bg-muted/30 border-b border-border">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  {cat}
                </h3>
              </div>
              <table className="min-w-full divide-y divide-border">
                <thead className="bg-muted/10">
                  <tr>
                    <Th>Rule name</Th>
                    <Th>Event</Th>
                    <Th>Recipients</Th>
                    <Th>Channels</Th>
                    <Th className="text-center">Active</Th>
                    <Th />
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {grouped.get(cat)!.map((r) => (
                    <tr
                      key={r.id}
                      className="hover:bg-muted/40 cursor-pointer"
                      onClick={() => navigate(`/admin/notifications/rules/${r.id}`)}
                    >
                      <Td>
                        <div className="font-medium">{r.name}</div>
                        {r.is_system && (
                          <span className="text-xs px-1 py-0.5 rounded bg-gray-100 text-gray-600 inline-block mt-0.5">
                            system
                          </span>
                        )}
                      </Td>
                      <Td className="text-xs">
                        <div>{r.trigger_event_label}</div>
                        <div className="font-mono text-muted-foreground">{r.trigger_event_code}</div>
                      </Td>
                      <Td className="text-xs">
                        {KIND_LABEL[r.recipient_kind]}
                        {r.recipient_kind === 'USER' && ` (${r.recipient_users.length})`}
                        {r.recipient_kind === 'ROLE' && ` (${r.recipient_roles.length} role${r.recipient_roles.length === 1 ? '' : 's'})`}
                        {r.recipient_kind === 'DYNAMIC' && r.recipient_resolver && (
                          <div className="font-mono text-muted-foreground mt-0.5">{r.recipient_resolver}</div>
                        )}
                      </Td>
                      <Td>
                        <div className="flex flex-wrap gap-1">
                          {r.channels.map((c) => (
                            <span key={c} className="text-xs px-1.5 py-0.5 rounded font-semibold bg-violet-100 text-violet-700">
                              {CHANNEL_LABEL[c] || c}
                            </span>
                          ))}
                        </div>
                      </Td>
                      <Td className="text-center" onClick={(ev) => ev.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={r.is_active}
                          onChange={(e) => toggleActiveMut.mutate({ id: r.id, value: e.target.checked })}
                          aria-label={r.is_active ? 'Deactivate rule' : 'Activate rule'}
                        />
                      </Td>
                      <Td className="text-right" onClick={(ev) => ev.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="p-1 rounded hover:bg-muted text-muted-foreground">
                              <MoreVertical className="h-4 w-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/admin/notifications/rules/${r.id}`)}>
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              disabled={r.is_system}
                              onClick={() => !r.is_system && confirm(
                                `rule "${r.name}"`,
                                () => deleteMut.mutate(r.id),
                              )}
                              className={!r.is_system ? 'text-destructive' : ''}
                            >
                              {r.is_system ? 'Delete (system rule)' : 'Delete'}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      <RecordDrawer
        open={creating}
        onOpenChange={(v) => { if (!v) setCreating(false) }}
        title="New notification rule"
        description="Pick the event that triggers this rule + give it a name. Recipients, channels, and templates are configured on the next page."
        onSubmit={() => createMut.mutate()}
        isPending={createMut.isPending}
        submitLabel="Create"
      >
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="rl-name">Rule name *</FieldLabel>
            <Input id="rl-name" autoFocus value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder='e.g. "Email recruiter when candidate accepts offer"' />
          </Field>
          <Field>
            <FieldLabel htmlFor="rl-event">Trigger event *</FieldLabel>
            <SearchableSelect
              id="rl-event"
              value={draft.trigger_event ? String(draft.trigger_event) : NONE}
              onChange={(v) => setDraft({ ...draft, trigger_event: v === NONE ? null : Number(v) })}
              placeholder="— pick an event —"
              options={[
                { value: NONE, label: '— pick an event —' },
                ...events.map((e) => ({
                  value: String(e.id),
                  label: `${e.category} · ${e.label}`,
                  hint: e.code,
                })),
              ]}
            />
            <FieldDescription>What has to happen for this rule to fire.</FieldDescription>
          </Field>
        </FieldGroup>
      </RecordDrawer>
    </AdminLayout>
  )
}

function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide ${className}`}>{children}</th>
}
function Td({ children, className = '', onClick }:
  { children?: ReactNode; className?: string; onClick?: (e: React.MouseEvent) => void }) {
  return <td className={`px-4 py-2 text-sm text-foreground ${className}`} onClick={onClick}>{children}</td>
}
